import {
  reactionButtonClass,
  reactionCountClass,
  reactionEmojiClass,
  reactionFloatingEmojiClass,
  reactionsContainerClass,
} from "@repo/fika-blog/src/reactions.css"
import { DEFAULT_EMOJIS } from "@repo/fika-shared/src/lib/emoji"
import { createMutation } from "@urql/solid"
import type { FragmentOf, ResultOf } from "gql.tada"
import { createMemo, createSignal, For, Show } from "solid-js"
import { createFragment, graphql } from "@/graphql"

export const ReactionsFragment = graphql(`
  fragment ReactionsFragment on PublishedPost {
    id
    reactions {
      emoji
      count
      reacted
    }
    publication {
      id
      reactionEmojis
    }
  }
`)

interface ReactionsProps {
  post: FragmentOf<typeof ReactionsFragment>
}

export function Reactions(props: ReactionsProps) {
  const post = createFragment(ReactionsFragment, () => props.post)

  const emojis = createMemo(() =>
    (post().publication.reactionEmojis ?? DEFAULT_EMOJIS).split(","),
  )

  return (
    <div class={reactionsContainerClass}>
      <For each={emojis()}>
        {emoji => <EmojiButton emoji={emoji} post={post} />}
      </For>
    </div>
  )
}

interface EmojiButtonProps {
  emoji: string
  post: () => ResultOf<typeof ReactionsFragment>
}

function EmojiButton(props: EmojiButtonProps) {
  const ToggleReactionMutation = graphql(
    `mutation ToggleReaction($postId: String!, $emoji: String!) {
      toggleReaction(postId: $postId, emoji: $emoji) {
        ...ReactionsFragment
      }
    }`,
    [ReactionsFragment],
  )
  const [, toggleReaction] = createMutation(ToggleReactionMutation)

  const reaction = createMemo(() =>
    props.post().reactions.find(r => r.emoji === props.emoji),
  )

  const count = createMemo(() => reaction()?.count ?? 0)
  const reacted = createMemo(() => reaction()?.reacted ?? false)
  const [justReacted, setJustReacted] = createSignal(false)

  const FLOAT_UP_DURATION_MS = 400

  function handleToggle(emoji: string) {
    const wasInactive = !reacted()
    if (wasInactive) setJustReacted(true)
    toggleReaction({
      postId: props.post().id,
      emoji,
    })
    if (wasInactive) {
      setTimeout(() => setJustReacted(false), FLOAT_UP_DURATION_MS)
    }
  }

  return (
    <button
      type="button"
      data-reaction-button
      data-just-reacted={justReacted() ? "true" : undefined}
      class={reactionButtonClass({
        active: reacted(),
      })}
      onClick={() => handleToggle(props.emoji)}
    >
      <span class={reactionEmojiClass}>{props.emoji}</span>
      <span class={reactionCountClass} data-count={count()}>
        <Show when={count()}>{count()}</Show>
      </span>
      <span class={reactionFloatingEmojiClass}>{props.emoji}</span>
    </button>
  )
}
